import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  FiZap,
  FiDownload,
  FiRefreshCw,
  FiCheckCircle,
  FiAlertCircle,
} from 'react-icons/fi';
import api from '../config/api';
import LoadingButton from './LoadingButton';
import { appAlert } from '../utils/appDialog';
import './ProfileResumeQualityPanel.css';

const SECTION_LABELS = {
  summary: 'Summary',
  skills: 'Skills',
  work_experience: 'Experience',
  education_history: 'Education',
  certifications: 'Certifications',
  languages: 'Languages',
  projects: 'Projects',
  links: 'Links',
};

function scoreTone(score) {
  if (score == null || score === '') return 'low';
  if (score >= 75) return 'good';
  if (score >= 50) return 'mid';
  return 'low';
}

function ScoreChip({ score, label, size = 'md' }) {
  const tone = scoreTone(score);
  return (
    <div className={`prq-chip prq-chip-${tone} prq-chip-${size}`} title={label}>
      <span className="prq-chip-value">{score != null ? score : '—'}</span>
      <span className="prq-chip-label">{label}</span>
    </div>
  );
}

function SectionDim({ dim, label }) {
  if (!dim) return null;
  const overall = typeof dim === 'number' ? dim : dim.overall;
  return (
    <div className={`prq-section-score prq-section-${scoreTone(overall)}`}>
      <div className="prq-section-score-head">
        <strong>{label}</strong>
        <span className="prq-section-overall">{overall ?? '—'}</span>
      </div>
      {typeof dim === 'object' && dim != null && (
        <div className="prq-section-dims">
          <span className={`prq-dim prq-dim-${scoreTone(dim.format)}`}>
            Format {dim.format ?? '—'}
          </span>
          <span className={`prq-dim prq-dim-${scoreTone(dim.grammar)}`}>
            Grammar {dim.grammar ?? '—'}
          </span>
          <span className={`prq-dim prq-dim-${scoreTone(dim.language)}`}>
            Language {dim.language ?? '—'}
          </span>
        </div>
      )}
    </div>
  );
}

async function downloadBlob(userId, format, templateId) {
  const res = await api.post(
    `/candidate-profiles/user/${userId}/generate-resume`,
    { format, template_id: templateId || undefined },
    { responseType: 'blob' }
  );
  const disposition = res.headers?.['content-disposition'] || '';
  const match = disposition.match(/filename="?([^";]+)"?/i);
  const filename = match?.[1] || `resume.${format === 'docx' ? 'docx' : 'pdf'}`;
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * AI quality scores + suggestions + PDF/DOCX download for candidate profile resume sections.
 */
export default function ProfileResumeQualityPanel({
  userId,
  sectionScores,
  profile,
  editing = false,
  onProfileChange,
  onScoresChange,
  canEdit = true,
  layout = 'inline',
  showQuality = true,
  showExport = true,
}) {
  const queryClient = useQueryClient();
  const [templateId, setTemplateId] = useState('');
  const [localScores, setLocalScores] = useState(sectionScores || null);

  const scores = localScores || sectionScores || null;

  const { data: templates = [] } = useQuery(
    ['resume-templates'],
    () => api.get('/resume-templates').then((r) => r.data),
    { staleTime: 60_000 }
  );

  const defaultTemplateId = useMemo(() => {
    const def = templates.find((t) => t.is_default) || templates[0];
    return def ? String(def.id) : '';
  }, [templates]);

  const effectiveTemplateId = templateId || defaultTemplateId;

  const analyzeMutation = useMutation(
    () => api.post(`/candidate-profiles/user/${userId}/analyze`).then((r) => r.data),
    {
      onSuccess: (data) => {
        setLocalScores(data);
        onScoresChange?.(data);
        queryClient.invalidateQueries(['candidate', userId]);
        queryClient.invalidateQueries(['candidates']);
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const autofillMutation = useMutation(
    () =>
      api
        .post(`/candidate-profiles/user/${userId}/autofill-from-resume`, {
          overwrite: true,
          analyze: true,
        })
        .then((r) => r.data),
    {
      onSuccess: (data) => {
        if (data.profile) onProfileChange?.(data.profile);
        if (data.section_scores) {
          setLocalScores(data.section_scores);
          onScoresChange?.(data.section_scores);
        }
        queryClient.invalidateQueries(['candidate', userId]);
        void appAlert('Profile sections filled from resume and scored.');
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const downloadMutation = useMutation(
    ({ format }) => downloadBlob(userId, format, effectiveTemplateId ? Number(effectiveTemplateId) : null),
    {
      onError: (e) => {
        const msg = e.response?.data?.error || e.message;
        // blob errors may need parsing
        if (e.response?.data instanceof Blob) {
          e.response.data.text().then((t) => {
            try {
              const j = JSON.parse(t);
              void appAlert(j.error || msg);
            } catch {
              void appAlert(msg);
            }
          });
          return;
        }
        void appAlert(msg);
      },
    }
  );

  const applySuggestion = (sug) => {
    if (!editing || !onProfileChange || !profile) {
      void appAlert('Switch to Edit Profile to apply suggestions.');
      return;
    }
    const text = (sug.proposed_text || '').trim();
    if (!text) {
      void appAlert(sug.suggestion || 'No proposed rewrite — edit manually using the tip.');
      return;
    }
    const next = { ...profile };
    const section = sug.section || sug.field;
    if (section === 'summary') next.summary = text;
    else if (section === 'skills') {
      next.skills = text
        .split(/[,;\n]/)
        .map((s) => s.trim())
        .filter(Boolean);
    } else if (section === 'work_experience') {
      const list = Array.isArray(next.work_experience) ? [...next.work_experience] : [];
      let idx = list.findIndex((e) => e.id && e.id === sug.item_id);
      if (idx < 0) idx = 0;
      if (list[idx]) {
        list[idx] = {
          ...list[idx],
          bullets: text
            .split(/\n/)
            .map((b) => b.replace(/^[-•*]\s*/, '').trim())
            .filter(Boolean),
        };
        next.work_experience = list;
      }
    } else if (section === 'education_history') {
      const list = Array.isArray(next.education_history) ? [...next.education_history] : [];
      let idx = list.findIndex((e) => e.id && e.id === sug.item_id);
      if (idx < 0) idx = 0;
      if (list[idx]) {
        list[idx] = { ...list[idx], degree: text };
        next.education_history = list;
      }
    } else if (section === 'projects') {
      const list = Array.isArray(next.projects) ? [...next.projects] : [];
      let idx = list.findIndex((e) => e.id && e.id === sug.item_id);
      if (idx < 0) idx = 0;
      if (list[idx]) {
        list[idx] = { ...list[idx], description: text };
        next.projects = list;
      }
    }
    onProfileChange(next);
  };

  if (!userId) return null;

  const sectionMap = scores?.section_scores || {};
  const suggestions = Array.isArray(scores?.suggestions) ? scores.suggestions : [];

  const isSidebar = layout === 'sidebar';
  const exportOnly = showExport && !showQuality;
  const title = exportOnly
    ? 'Template & export'
    : isSidebar
      ? 'Resume quality'
      : 'Resume quality & export';

  return (
    <div
      className={[
        'prq-panel',
        isSidebar ? 'prq-panel--sidebar scroll-auto-hide' : '',
        exportOnly ? 'prq-panel--export' : '',
      ].filter(Boolean).join(' ')}
    >
      <div className="prq-toolbar">
        <div className="prq-toolbar-title">
          {exportOnly ? <FiDownload aria-hidden /> : <FiZap aria-hidden />} {title}
        </div>
        {showQuality && (
          <div className="prq-toolbar-actions">
            {canEdit && (
              <LoadingButton
                type="button"
                className="btn btn-secondary btn-sm"
                loading={autofillMutation.isLoading}
                onClick={() => autofillMutation.mutate()}
                title="AI-parse the latest uploaded resume into profile sections"
              >
                <FiRefreshCw aria-hidden /> {isSidebar ? 'Autofill' : 'Autofill from resume'}
              </LoadingButton>
            )}
            <LoadingButton
              type="button"
              className="btn btn-secondary btn-sm"
              loading={analyzeMutation.isLoading}
              onClick={() => analyzeMutation.mutate()}
            >
              <FiZap aria-hidden /> {isSidebar ? 'Score' : 'Score sections'}
            </LoadingButton>
          </div>
        )}
      </div>

      {showQuality && isSidebar && !scores && (
        <p className="prq-empty-hint">
          Score this profile to see section scores, strengths, gaps, and rewrite suggestions beside the content.
        </p>
      )}

      {showQuality && scores && (
        <div className="prq-overview">
          <ScoreChip score={scores.overall_score} label="Overall" size="lg" />
          <ScoreChip score={scores.format_score} label="Format" />
          <ScoreChip score={scores.grammar_score} label="Grammar" />
          <ScoreChip score={scores.language_score} label="Language" />
        </div>
      )}

      {showQuality && scores?.summary && <p className="prq-summary">{scores.summary}</p>}

      {showQuality && Object.keys(sectionMap).length > 0 && (
        <div className="prq-section-grid">
          {Object.entries(sectionMap).map(([key, dim]) => (
            <SectionDim key={key} dim={dim} label={SECTION_LABELS[key] || key} />
          ))}
        </div>
      )}

      {showQuality && (scores?.strengths?.length > 0 || scores?.gaps?.length > 0) && (
        <div className="prq-lists">
          {scores.strengths?.length > 0 && (
            <div>
              <h5>
                <FiCheckCircle aria-hidden /> Strengths
              </h5>
              <ul>
                {scores.strengths.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            </div>
          )}
          {scores.gaps?.length > 0 && (
            <div>
              <h5>
                <FiAlertCircle aria-hidden /> Gaps
              </h5>
              <ul>
                {scores.gaps.map((g) => (
                  <li key={g}>{g}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {showQuality && suggestions.length > 0 && (
        <div className="prq-suggestions">
          <h5>Suggestions to improve scores</h5>
          <ul className="prq-suggestion-list">
            {suggestions.map((sug, idx) => (
              <li
                key={sug.id || `${sug.section || 'section'}-${sug.issue || idx}-${idx}`}
                className={`prq-sug prq-sug-${sug.severity || 'medium'}`}
              >
                <div className="prq-sug-meta">
                  <span className="prq-sug-section">
                    {SECTION_LABELS[sug.section] || sug.section}
                  </span>
                  <span className={`prq-sug-dim prq-dim-${scoreTone(50)}`}>
                    {sug.dimension || 'language'}
                  </span>
                  <span className={`prq-sug-sev prq-sev-${sug.severity}`}>{sug.severity}</span>
                </div>
                <p className="prq-sug-issue">{sug.issue}</p>
                {sug.suggestion && <p className="form-hint">{sug.suggestion}</p>}
                {sug.proposed_text && (
                  <pre className="prq-sug-proposed">{sug.proposed_text}</pre>
                )}
                {canEdit && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => applySuggestion(sug)}
                  >
                    Apply rewrite
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {showExport && (
        <div className={`prq-export${exportOnly ? ' prq-export--standalone' : ''}`}>
          <label htmlFor="prq-template">Resume template</label>
          <select
            id="prq-template"
            value={effectiveTemplateId}
            onChange={(e) => setTemplateId(e.target.value)}
          >
            {templates.length === 0 && <option value="">Default layout</option>}
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
                {t.is_default ? ' (default)' : ''}
              </option>
            ))}
          </select>
          <p className="prq-export-hint">
            Choose a template, then download a PDF or DOCX built from this profile’s resume content.
          </p>
          <div className="prq-export-btns">
            <LoadingButton
              type="button"
              className="btn btn-primary btn-sm"
              loading={downloadMutation.isLoading && downloadMutation.variables?.format === 'pdf'}
              onClick={() => downloadMutation.mutate({ format: 'pdf' })}
            >
              <FiDownload aria-hidden /> Download PDF
            </LoadingButton>
            <LoadingButton
              type="button"
              className="btn btn-secondary btn-sm"
              loading={downloadMutation.isLoading && downloadMutation.variables?.format === 'docx'}
              onClick={() => downloadMutation.mutate({ format: 'docx' })}
            >
              <FiDownload aria-hidden /> Download DOCX
            </LoadingButton>
          </div>
        </div>
      )}
    </div>
  );
}

export { ScoreChip, scoreTone, SECTION_LABELS };
